import {
	LoadingOutlined,
	CheckOutlined,
	DownOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { AgentTranscriptEntry } from '../../../types/agentHarness';
import { humanizeToolName, uncapitalize } from '../../utils/OscarLabels';

type ToolEntry = Extract<AgentTranscriptEntry, { kind: 'tool' }>;

interface OscarToolTraceProps {
	/** One run of consecutive tool calls, in the order they were made. */
	tools: ToolEntry[];
	className?: string;
}

/**
 * Turns a run of tool calls into one collapsible line.
 *
 * Reads run without asking, so a single question can fire several before the
 * answer arrives — rendered individually they pushed the answer off screen. The
 * payload is deliberately not shown at all: a row states what ran and how it
 * came back, and nothing more.
 */
/**
 * Anything that looks like a serialized payload rather than a sentence. The
 * harness sends a *truncated* preview as `summary`, so a large result arrives as
 * JSON that no longer parses — which is how `{ "items": [ { "id": "273768…`
 * ended up on screen as a tool's outcome.
 */
const looksLikePayload = (text: string): boolean => {
	const trimmed = text.trim();
	return (
		trimmed.charAt(0) === '{' ||
		trimmed.charAt(0) === '[' ||
		trimmed.indexOf('":') !== -1 ||
		trimmed.indexOf('\\n') !== -1
	);
};

const outcomeFor = (tool: ToolEntry): string => {
	if (!tool.done) {
		return 'running…';
	}
	if (tool.isError) {
		return 'failed';
	}
	if (!tool.summary) {
		return 'done';
	}
	try {
		const parsed = JSON.parse(tool.summary);
		if (parsed && Array.isArray(parsed.items)) {
			const total = typeof parsed.total === 'number' ? parsed.total : parsed.items.length;
			return `${total} result${total === 1 ? '' : 's'}`;
		}
		if (parsed && typeof parsed.stdout === 'string' && parsed.stdout.trim()) {
			return parsed.stdout.trim().split('\n')[0];
		}
		if (parsed && parsed.ok === true) {
			return 'ok';
		}
	} catch (parseError) {
		// Not parseable — usually a payload the harness already truncated.
	}
	// Never fall back to the payload itself. A row says what ran and how it came
	// back; if the result cannot be described in a few words, "done" is the
	// honest version and the answer below is where the content belongs.
	if (looksLikePayload(tool.summary)) {
		return 'done';
	}
	return tool.summary.length > 40 ? `${tool.summary.slice(0, 40)}…` : tool.summary;
};

export const OscarToolTrace = ({ tools, className }: OscarToolTraceProps) => {
	const running = tools.some((tool) => !tool.done);
	const failed = tools.some((tool) => tool.isError);
	// Lower case: the summary is ambient status, like the thinking indicator's
	// label. The tool names inside are names, so they keep their case.
	const label = running
		? `running ${
				tools.length === 1 ? uncapitalize(humanizeToolName(tools[0].name)) : `${tools.length} tools`
		  }…`
		: `ran ${tools.length} tool${tools.length === 1 ? '' : 's'}`;

	return (
		<details className={cn('group w-full min-w-0', className)}>
			<summary
				className="flex cursor-pointer list-none items-center gap-2xs text-character-secondary
					[&::-webkit-details-marker]:hidden"
			>
				<DownOutlined
					// See OscarDisclosure: `rotate-180` cannot work with preflight off.
					className="shrink-0 text-[10px] transition-transform group-open:[transform:rotate(180deg)]
						motion-reduce:transition-none"
				/>
				<span
					className={cn(
						'footnoteregular',
						running && 'animate-pulse motion-reduce:animate-none',
						failed && 'text-character-danger'
					)}
				>
					{label}
				</span>
			</summary>
			{/* A rail down the left ties the run together as one thing. */}
			<div className="ml-[5px] flex flex-col gap-2xs border-0 border-l border-solid border-grey-4 py-2xs pl-s">
				{tools.map((tool) => (
					// The raw identifier stays on the title: when the agent does something
					// unexpected, the exact tool is the first thing anyone asks for.
					<div key={tool.id} title={tool.name} className="flex items-center gap-2xs">
						{/* Not a ✕: that reads as a dismiss control on a row nobody can
						    dismiss. An exclamation says failed and invites no click, and
						    keeps the column aligned with the ticks above it. */}
						{tool.isError ? (
							<ExclamationCircleOutlined className="shrink-0 text-[10px] text-character-danger" />
						) : tool.done ? (
							<CheckOutlined className="shrink-0 text-[10px] text-character-secondary" />
						) : (
							/* A tick beside "running…" claims an outcome the tool has not
							   reached yet, which matters most on the writes this trace
							   exists to make auditable. */
							<LoadingOutlined className="shrink-0 text-[10px] text-character-secondary" />
						)}
						<span
							// Footnote, like the outcome beside it and the summary above it:
							// everything inside a disclosure is secondary by definition.
							className={cn(
								'footnoteregular truncate',
								tool.isError ? 'text-character-danger' : 'text-grey-9'
							)}
						>
							{humanizeToolName(tool.name)}
						</span>
						<span className="footnoteregular min-w-0 flex-1 truncate text-character-secondary">
							{outcomeFor(tool)}
						</span>
					</div>
				))}
			</div>
		</details>
	);
};

export default OscarToolTrace;
